import React, { useState } from 'react';
import { Box, Warehouse, Truck, Check, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { pricingItems, includedFeatures, otherServices } from '../data/mock';
import { trackCtaClick } from '../lib/analytics';

const iconMap = { Box, Warehouse, Truck };

export default function Pricing() {
  const [otherOpen, setOtherOpen] = useState(false);

  return (
    <section id="pricing" className="py-20 sm:py-28 bg-slate-50/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Simple, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            Here&apos;s exactly what fulfillment costs. No surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {pricingItems.map((item) => {
            const Icon = iconMap[item.icon];
            const isHighlighted = item.highlighted;
            return (
              <div
                key={item.title}
                className={`relative rounded-2xl p-7 border transition-all duration-200 ${
                  isHighlighted
                    ? 'brand-gradient text-white border-transparent shadow-xl shadow-indigo-500/20'
                    : 'bg-white border-slate-200 text-slate-900 hover:border-indigo-200 hover:shadow-lg'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    isHighlighted ? 'bg-white/20' : 'bg-indigo-50'
                  }`}
                >
                  <Icon
                    className={`w-6 h-6 ${isHighlighted ? 'text-white' : 'text-indigo-600'}`}
                    strokeWidth={2}
                  />
                </div>
                <p
                  className={`text-sm font-semibold mb-2 ${
                    isHighlighted ? 'text-indigo-100' : 'text-slate-500'
                  }`}
                >
                  {item.title}
                </p>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-4xl font-extrabold tracking-tight">{item.price}</span>
                  {item.unit && (
                    <span
                      className={`text-sm font-medium ${
                        isHighlighted ? 'text-indigo-100' : 'text-slate-500'
                      }`}
                    >
                      {item.unit}
                    </span>
                  )}
                </div>
                <p className={`text-sm ${isHighlighted ? 'text-indigo-50' : 'text-slate-600'}`}>
                  {item.note}
                </p>
                {item.subnote && (
                  <p className="mt-1 text-sm font-semibold text-indigo-600">{item.subnote}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* What's included */}
        <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-8">
          <p className="text-center text-sm font-semibold uppercase tracking-widest text-slate-500 mb-6">
            What&apos;s Included
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {includedFeatures.map((feature) => (
              <div key={feature} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Check className="w-3 h-3 text-indigo-600" strokeWidth={3} />
                </span>
                <span className="text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Other services accordion */}
        <div className="mt-5 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <button
            onClick={() => setOtherOpen(!otherOpen)}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-indigo-50/40 transition-colors duration-150"
          >
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
                <ChevronDown
                  className={`w-5 h-5 text-indigo-600 transition-transform duration-200 ${
                    otherOpen ? 'rotate-180' : ''
                  }`}
                />
              </span>
              <span className="text-lg font-bold text-slate-900">Other Services</span>
            </div>
          </button>
          {otherOpen && (
            <div className="px-6 pb-6 border-t border-slate-100">
              <div className="divide-y divide-slate-100">
                {otherServices.map((s) => (
                  <div key={s.service} className="flex items-center justify-between py-3">
                    <span className="text-slate-700">{s.service}</span>
                    <span className="font-semibold text-slate-900">{s.price}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-10 flex flex-col items-center gap-2">
          <Button
            asChild
            size="lg"
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-8 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
          >
            <a
              href="#contact"
              className="inline-flex items-center gap-2"
              onClick={() => trackCtaClick({ label: 'Get Started', location: 'pricing', destination: '#contact' })}
              data-testid="pricing-cta-get-started"
            >
              Get Started
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
          </Button>
          <p className="text-sm text-slate-500">No contracts. Cancel anytime.</p>
        </div>
      </div>
    </section>
  );
}
